import { useRouter } from "@tanstack/react-router"
import { type ReactNode, useEffect, useState } from "react"

import { requestPasswordReset } from "../../api/auth-reset.api"
import { isApiProblem } from "../../api/problem"
import { formatCountdown } from "./login-state"
import { Button } from "../../ui/button"
import { TextField } from "../../ui/field"
import styles from "./auth-screen.module.css"

/**
 * 1-Э1.3 «Восстановление доступа» — /forgot-password.
 *
 * ОТДЕЛЬНАЯ СТРАНИЦА, А НЕ ДЕЙСТВИЕ НА ФОРМЕ ВХОДА. У неё свой адрес: ссылку «вот сюда
 * жми, если не помнишь пароль» присылают человеку в переписке, и она обязана открыться
 * той же страницей. Плюс здесь своё состояние — введённый адрес, отказ, ожидание — и на
 * форме входа оно путалось с состоянием самого входа.
 *
 * ОТВЕТ НЕЙТРАЛЬНЫЙ ВСЕГДА. Сервер отвечает одинаково, есть такой адрес или нет
 * (auth.controller: всегда 202), и экран обязан говорить то же: «если такой адрес есть
 * в системе, письмо придёт». Разный текст превратил бы форму в перечислитель
 * сотрудников салона — вводи адреса и смотри, какие «нашлись».
 *
 * ТЕЛЕФОН ПОКА НЕ ПРИНИМАЕТСЯ, и это сказано прямо. Восстановление по СМС упирается
 * в выбор оператора (вопрос владельца), а молчаливое «введите почту» на попытку ввести
 * телефон читается как поломка.
 */
export function ForgotPasswordScreen(props: { readonly email?: string | undefined }): ReactNode {
  const router = useRouter()
  const [credential, setCredential] = useState(props.email ?? "")
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [retryAfter, setRetryAfter] = useState<number | null>(null)

  /*
   * ОТСЧЁТ ИДЁТ, А НЕ ЗАСТЫВАЕТ. Сервер называет остаток один раз, и число, записанное
   * в состояние, через три секунды становится враньём: человек смотрит на «осталось
   * 873 с» минуту и не понимает, почему оно не меняется. Плюс секунды — не та единица,
   * в которой люди ждут: «14:33» читается сразу, «873 с» надо делить в уме.
   */
  useEffect(() => {
    if (retryAfter === null || retryAfter <= 0) return
    const timer = setInterval(() => {
      setRetryAfter((left) => (left === null || left <= 1 ? null : left - 1))
    }, 1000)
    return () => {
      clearInterval(timer)
    }
  }, [retryAfter])

  const looksLikePhone = /^[+\d][\d\s()-]{6,}$/u.test(credential.trim())
  const looksLikeEmail = credential.includes("@") && credential.trim().length >= 5

  const submit = async (): Promise<void> => {
    setBusy(true)
    setFailure(null)
    setRetryAfter(null)
    try {
      await requestPasswordReset(credential.trim())
      setSent(true)
    } catch (error) {
      if (!isApiProblem(error)) throw error
      if (error.code === "rate_limited") {
        // Срок повтора приходит полем retryAfter (и заголовком Retry-After). В разборе
        // ответа его отдельного поля нет, поэтому берём из тела целиком: «слишком часто»
        // без срока человек читает как «сломалось» и жмёт ещё десять раз.
        const seconds = error.raw["retryAfter"]
        setRetryAfter(typeof seconds === "number" ? seconds : null)
      }
      // ОБРЫВ СВЯЗИ НАЗЫВАЕТСЯ ОБРЫВОМ. Общий текст отказа про «показанные данные»
      // здесь читается как «письмо не ушло», а на деле запрос мог и дойти: пока это
      // неизвестно, человеку надо сказать именно это, иначе он жмёт ещё раз и тратит
      // одну из трёх попыток.
      if (error.code === "offline" || error.code === "timeout") {
        setFailure(
          "Связь прервалась. Неизвестно, успел ли уйти запрос: проверьте почту через минуту, прежде чем просить ссылку снова — попыток три за четверть часа.",
        )
        return
      }
      setFailure(error.detail ?? error.title)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <button
          type="button"
          className={styles.backLink}
          onClick={() => {
            void router.navigate({ to: "/login" })
          }}
        >
          ‹ Вернуться ко входу
        </button>

        <h1 className={styles.title}>Восстановление доступа</h1>

        {sent ? (
          <div role="status" aria-live="polite">
            {/*
              * Текст НЕ подтверждает, что учётка есть: «письмо ушло на a•••@mail.ru»
              * было бы ответом на вопрос, которого человеку задавать не разрешено.
              */}
            <p className={styles.text}>
              Если такой адрес есть в системе, письмо со ссылкой уже в пути. Ссылка
              действует 15 минут и открывается один раз.
            </p>
            <p className={styles.hint}>
              Письма нет через пару минут — проверьте папку «Спам» и адрес: в нём легко
              ошибиться на одну букву. Повторный запрос гасит прежнюю ссылку.
            </p>
            <div className={styles.actions}>
              <Button
                variant="secondary"
                onClick={() => {
                  setSent(false)
                }}
              >
                Ввести другой адрес
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  void router.navigate({ to: "/login" })
                }}
              >
                Ко входу
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className={styles.text}>
              Введите рабочую почту — ту, по которой входите в кабинет. Пришлём ссылку,
              по которой можно задать новый пароль.
            </p>

            <TextField
              label="Почта"
              type="email"
              value={credential}
              autoComplete="email"
              onChange={(event) => {
                setCredential(event.currentTarget.value)
              }}
            />

            {looksLikePhone ? (
              <p className={styles.hint}>
                Это похоже на телефон. Восстановление по СМС пока не работает: оператор
                для отправки ещё не выбран. Введите почту, по которой входите в кабинет,
                — или попросите владельца салона сменить вам пароль.
              </p>
            ) : null}

            {failure === null ? null : (
              <p className={styles.failure} role="alert">
                {failure}
                {retryAfter === null ? "" : ` Повторить можно через ${formatCountdown(retryAfter)}.`}
              </p>
            )}

            <div className={styles.actions}>
              <Button
                variant="primary"
                loading={busy}
                loadingLabel="Отправляем…"
                deniedReason={looksLikeEmail ? undefined : "Введите адрес почты целиком"}
                onClick={() => {
                  void submit()
                }}
              >
                Прислать ссылку
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
